import {
  getTotalMatches,
  getAverages,
  getTotalStats,
  getTotalUsers,
  getAveragesPhases
} from './getData'
import i18n from '@/i18n/i18n'

const stats = {
  namespaced: true,
  state: {
    totalMatches: {},
    stats: {},
    totalStats: {},
    totalUsers: {},
    averagesPhases: {},
    loading: false
  },
  getters: {
    averageStats: state => {
      if (!Object.keys(state.stats).length) return null
      let stats = state.stats.averages.map(stat => ({
        name: stat.name,
        [i18n.t('average')]: stat.average
      }))
      return {
        stats,
        title: state.stats.title
      }
    },
    matchesStats: state => {
      if (!Object.keys(state.stats).length || !state.stats.matches) return []
      return state.stats.matches.map(matchStat => {
        let stats = matchStat.stats.map(stat => ({
          name: stat.name,
          [matchStat.participant]: stat.score,
          [i18n.t('average')]: stat.average
        }))
        return {
          stats,
          title: matchStat.title,
          participant: matchStat.participant
        }
      })
    },
    matchesStatsByAct: state => {
      if (
        !Object.keys(state.stats).length ||
        !state.stats.matches ||
        !state.stats.actStats
      ) {
        return []
      }
      return state.stats.matches.map(matchStat => {
        let stats = matchStat.stats.map(stat => {
          let result = {
            name: stat.name,
            [matchStat.participant]: stat.score
          }
          let averageAct = state.stats.actStats.averages.find(
            actStat =>
              (stat.categoryId && actStat.categoryId === stat.categoryId) ||
              (stat.typeId && actStat.typeId === stat.typeId)
          )
          result[i18n.t('average')] = averageAct ? averageAct.average : 0
          return result
        })
        return {
          stats,
          title: matchStat.title,
          participant: matchStat.participant
        }
      })
    },
    matchesStatsByTournament: state => {
      if (
        !Object.keys(state.stats).length ||
        !state.stats.matches ||
        !state.stats.tournamentStats
      ) {
        return []
      }
      return state.stats.matches.map(matchStat => {
        let stats = matchStat.stats.map(stat => {
          let result = {
            name: stat.name,
            [matchStat.participant]: stat.score
          }
          let averageTour = state.stats.tournamentStats.averages.find(
            tourStat =>
              (stat.categoryId && tourStat.categoryId === stat.categoryId) ||
              (stat.typeId && tourStat.typeId === stat.typeId)
          )
          result[i18n.t('average')] = averageTour ? averageTour.average : 0
          return result
        })
        return {
          stats,
          title: matchStat.title,
          participant: matchStat.participant
        }
      })
    },
    averagesPhases: state => {
      if (!Object.keys(state.averagesPhases).length) return null
      let averagesPhases = {}
      // title
      averagesPhases.title = state.averagesPhases.title
      // xAxisLabel
      averagesPhases.phases = state.averagesPhases.phases
      // series
      averagesPhases.series = []
      // Legend
      averagesPhases.categories = []
      state.averagesPhases.categories.forEach(category => {
        averagesPhases.categories.push(category.name)
        let serie = {
          name: category.name,
          type: 'line',
          data: []
        }
        state.averagesPhases.averages.forEach(phase => {
          let categoryScore = phase.categories.find(
            category2 => category2._id == category._id
          )
          if (phase.categories.some(item => item.score > 0)) {
            serie.data.push(categoryScore.score)
          }
        })
        averagesPhases.series.push(serie)
      })
      state.averagesPhases.types.forEach(type => {
        averagesPhases.categories.push(type.name)
        let serie = {
          name: type.name,
          type: 'line',
          data: []
        }
        state.averagesPhases.averages.forEach(phase => {
          let typeScore = phase.types.find(type2 => type2._id == type._id)
          if (phase.types.some(item => item.score > 0)) {
            serie.data.push(typeScore.score)
          }
        })
        averagesPhases.series.push(serie)
      })
      return averagesPhases
    },
    wonLostResult: state => {
      if (!Object.keys(state.totalMatches).length) return []
      return [
        {
          [i18n.t('total')]: i18n.t('won'),
          value: state.totalMatches.won
        },
        {
          [i18n.t('total')]: i18n.t('lost'),
          value: state.totalMatches.lost
        }
      ]
    },
    stanceResult: state => {
      if (!Object.keys(state.totalMatches).length) return []
      return [
        {
          [i18n.t('total')]: i18n.t('homeTeamTitle'),
          value: state.totalMatches.favor
        },
        {
          [i18n.t('total')]: i18n.t('visitorTeamTitle'),
          value: state.totalMatches.against
        }
      ]
    },
    usersStats: state => {
      if (!Object.keys(state.totalUsers).length) return []

      let result = [
        {
          [i18n.t('total')]: i18n.t('active'),
          value: state.totalUsers.active || 0
        }
      ]

      if (state.totalUsers.notActive) {
        result.push({
          [i18n.t('total')]: i18n.t('notActive'),
          value: state.totalUsers.notActive || 0
        })
      }
      if (state.totalUsers.deleted) {
        result.push({
          [i18n.t('total')]: i18n.t('deleted'),
          value: state.totalUsers.deleted || 0
        })
      }
      return result
    }
  },
  mutations: {
    updateTotalMatches: (state, stats) => {
      state.totalMatches = stats || {}
    },
    updateAverages: (state, stats) => {
      state.stats = stats || {}
    },
    updateTotalStats: (state, stats) => {
      state.totalStats = stats || {}
    },
    updateTotalUsers: (state, stats) => {
      state.totalUsers = stats || {}
    },
    updatePhasesAverages: (state, stats) => {
      state.averagesPhases = stats || {}
    },
    updateLoading: (state, loading) => (state.loading = loading),
    updateClear: state => {
      state.totalMatches = {}
      state.stats = {}
      state.totalStats = {}
      state.totalUsers = {}
      state.averagesPhases = {}
    }
  },
  actions: {
    setTotalMatches: async ({ commit }, params) => {
      let totalMatches = await getTotalMatches(commit, params)
      commit('updateTotalMatches', totalMatches)
    },
    setAverages: async ({ commit }, params) => {
      let averages = await getAverages(commit, params)
      commit('updateAverages', averages)
    },
    setTotalStats: async ({ commit }) => {
      let totalStats = await getTotalStats(commit)
      commit('updateTotalStats', totalStats)
    },
    setTotalUsers: async ({ commit }, params) => {
      let totalUsers = await getTotalUsers(commit, params)
      commit('updateTotalUsers', totalUsers)
    },
    setAveragesPhases: async ({ commit }, params) => {
      let averagesPhases = await getAveragesPhases(commit, params)
      commit('updatePhasesAverages', averagesPhases)
    },
    setClear: ({ commit }) => {
      commit('updateClear')
    },
    setClearAverages: ({ commit }) => {
      commit('updateAverages', {})
    },
    setClearUsersStats: ({ commit }) => {
      commit('updateTotalUsers', {})
    },
    setClearPhasesAverages: ({ commit }) => {
      commit('updatePhasesAverages', {})
    }
  }
}

export default stats
